import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { BedDouble, Ruler, MapPin, Zap, Home as HomeIcon, ChevronLeft, Heart, CheckCircle2, AlertCircle, X, Calendar, Coins, Send, FileText, Clock, Download } from "lucide-react";
import JsonLd from "@/components/JsonLd";
import BidModal from "@/components/BidModal";
import { http, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";

const eur = (n) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

export default function PropertyDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [active, setActive] = useState(0);
  const [match, setMatch] = useState(null);
  const [modal, setModal] = useState(null); // "interest" | "viewing" | "offer" | "slot"
  const [slotToBook, setSlotToBook] = useState(null);
  const [docs, setDocs] = useState([]);
  const [slots, setSlots] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", preferred_date: "", preferred_time: "middag", amount: "", financing: "hypotheek_nodig", financing_reservation: true, transfer_date: "", conditions: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { http.get(`/properties/${id}`).then((r) => setP(r.data)).catch(() => setP(false)); }, [id]);
  useEffect(() => {
    if (p && p.id) {
      http.get(`/properties/${p.id}/documents`).then((r) => setDocs(r.data)).catch(() => setDocs([]));
      http.get(`/properties/${p.id}/slots`).then((r) => setSlots(r.data)).catch(() => setSlots([]));
    }
    const pid = new URLSearchParams(window.location.search).get("profile");
    if (pid && p && p.id) http.get(`/property/${p.id}/match-for/${pid}`).then((r) => setMatch(r.data)).catch(() => {});
  }, [p]);

  if (p === null) return <div className="max-w-6xl mx-auto p-10">Laden…</div>;
  if (p === false) return <div className="max-w-6xl mx-auto p-10">Woning niet gevonden.</div>;

  const photos = (p.photos || []).map((x) => x.data_url || x.url).filter(Boolean);
  const primary = photos[active] || photos[0];

  const submitInteraction = async () => {
    setBusy(true);
    try {
      if (modal === "slot" && slotToBook) {
        const { data } = await http.post(`/slots/${slotToBook.id}/book`, { name: form.name, email: form.email, phone: form.phone, notes: form.message });
        toast.success(data.message || "Bezichtiging bevestigd");
        setSlots((s) => s.filter((x) => x.id !== slotToBook.id || (x.bookings_count + 1 < x.max_bookings)));
        setModal(null); setSlotToBook(null);
        setBusy(false); return;
      }
      const url = { interest: "/interests", viewing: "/viewings" }[modal];
      if (!url) { setBusy(false); return; }
      const payload = { property_id: p.id, name: form.name, email: form.email, phone: form.phone };
      if (modal === "interest") Object.assign(payload, { message: form.message });
      if (modal === "viewing") Object.assign(payload, { preferred_date: form.preferred_date, preferred_time: form.preferred_time, notes: form.message });
      const { data } = await http.post(url, payload);
      toast.success(data.message || "Bedankt, wij nemen contact op.");
      setModal(null);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const DOC_LABELS = { energielabel: "Energielabel", eigendomsbewijs: "Eigendomsbewijs", tekening: "Plattegrond / tekening", vve: "VvE-stukken", keuring: "Keuringsrapport", overig: "Overig" };

  const propertyTypeToSchema = (t) => {
    const map = { appartement: "Apartment", tussenwoning: "House", hoekwoning: "House",
                  "2-onder-1-kap": "House", vrijstaand: "SingleFamilyResidence",
                  bungalow: "SingleFamilyResidence", nieuwbouw: "House" };
    return map[t] || "Residence";
  };
  const address = {
    "@type": "PostalAddress",
    "streetAddress": p.address,
    "postalCode": p.postal_code,
    "addressLocality": p.city,
    "addressCountry": p.country || "NL"
  };
  const accommodation = {
    "@type": propertyTypeToSchema(p.property_type),
    "name": p.title,
    "address": address,
    "numberOfRooms": p.rooms,
    "numberOfBedrooms": p.bedrooms,
    "floorSize": {"@type": "QuantitativeValue", "value": p.area_m2, "unitCode": "MTK"},
    ...(p.year_built ? {"yearBuilt": p.year_built} : {}),
    ...(p.features?.length ? {"amenityFeature": p.features.map(f => ({"@type": "LocationFeatureSpecification", "name": f, "value": true}))} : {}),
  };
  const ld = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    "name": p.title,
    "url": window.location.href,
    "description": p.description,
    "image": photos.slice(0, 5),
    "datePosted": p.published_at || p.created_at || new Date().toISOString(),
    "address": address,
    "provider": {"@type": "Organization", "name": "Woon Verder", "url": window.location.origin},
    "about": accommodation,
    "mainEntity": accommodation,
    "offers": {
      "@type": "Offer",
      "price": p.price,
      "priceCurrency": p.currency || "EUR",
      "availability": "https://schema.org/InStock",
      "url": window.location.href,
      "priceValidUntil": new Date(Date.now() + 90*24*3600*1000).toISOString().slice(0,10),
      "seller": {"@type": "Organization", "name": "Woon Verder"},
      "businessFunction": "http://purl.org/goodrelations/v1#Sell"
    },
    ...(p.energy_label ? {"additionalProperty": [{"@type": "PropertyValue", "name": "energyRating", "value": p.energy_label}]} : {}),
  };
  const breadLd = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    "itemListElement": [
      {"@type": "ListItem", "position": 1, "name": "Home", "item": window.location.origin + "/"},
      {"@type": "ListItem", "position": 2, "name": "Woningen", "item": window.location.origin + "/woningen"},
      {"@type": "ListItem", "position": 3, "name": p.title, "item": window.location.href},
    ]
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
      <JsonLd data={ld} id="ld-prop" />
      <JsonLd data={breadLd} id="ld-bread" />
      <Link to="/woningen" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="detail-back">
        <ChevronLeft className="w-4 h-4" /> Terug naar aanbod
      </Link>

      <div className="mt-4 grid md:grid-cols-5 gap-4">
        <div className="md:col-span-3 aspect-[4/3] rounded-2xl overflow-hidden bg-slate-100">
          {primary && <img src={primary} alt={p.title} className="w-full h-full object-cover" />}
        </div>
        <div className="md:col-span-2 grid grid-cols-2 gap-3">
          {photos.slice(0, 4).map((u, i) => (
            <button key={i} onClick={() => setActive(i)} className={`aspect-[4/3] rounded-xl overflow-hidden ${i === active ? "ring-2 ring-[color:var(--wv-navy)]" : ""}`} data-testid={`thumb-${i}`}>
              <img src={u} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <p className="text-xs font-bold uppercase tracking-widest text-[color:var(--wv-green-dark)]">{p.property_type}</p>
          <h1 className="font-display mt-1 text-3xl md:text-4xl font-extrabold text-[color:var(--wv-obsidian)]" data-testid="detail-title">{p.title}</h1>
          <p className="mt-2 inline-flex items-center gap-1 text-slate-600"><MapPin className="w-4 h-4" /> {p.address}, {p.postal_code} {p.city}</p>
          {match && !match.hard_fail && (
            <div className="mt-5 p-5 rounded-2xl bg-gradient-to-br from-[color:var(--wv-green)]/10 to-[color:var(--wv-navy)]/5 border border-[color:var(--wv-green)]/30" data-testid="match-explain-box">
              <div className="flex items-center gap-3">
                <div className="wv-badge-match text-sm">{match.score}% Match</div>
                <p className="font-display font-bold text-lg">Waarom past deze woning bij u?</p>
              </div>
              <ul className="mt-3 space-y-1 text-sm">
                {match.matched.map((m, i) => <li key={i} className="flex items-start gap-2 text-slate-800"><CheckCircle2 className="w-4 h-4 text-[color:var(--wv-green)] mt-0.5" /> {m}</li>)}
                {match.deviations.map((d, i) => <li key={`d${i}`} className="flex items-start gap-2 text-slate-600"><AlertCircle className="w-4 h-4 text-amber-500 mt-0.5" /> {d}</li>)}
              </ul>
            </div>
          )}
          <div className="mt-6 flex items-center gap-6 text-slate-700">
            <span className="inline-flex items-center gap-2"><BedDouble className="w-5 h-5" /> <span className="font-mono-num font-semibold">{p.bedrooms}</span> slaapkamers</span>
            <span className="inline-flex items-center gap-2"><Ruler className="w-5 h-5" /> <span className="font-mono-num font-semibold">{p.area_m2}</span> m²</span>
            <span className="inline-flex items-center gap-2"><HomeIcon className="w-5 h-5" /> <span className="font-mono-num font-semibold">{p.rooms}</span> kamers</span>
            {p.energy_label && <span className="inline-flex items-center gap-2"><Zap className="w-5 h-5 text-[color:var(--wv-green)]" /> <span className="font-mono-num font-semibold">{p.energy_label}</span></span>}
          </div>
          <div className="mt-8">
            <h2 className="font-display text-xl font-bold">Beschrijving</h2>
            <p className="mt-3 text-slate-700 leading-relaxed whitespace-pre-line" data-testid="detail-description">{p.description}</p>
          </div>
          {p.features?.length > 0 && (
            <div className="mt-8">
              <h2 className="font-display text-xl font-bold">Kenmerken</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {p.features.map((f) => <span key={f} className="wv-chip capitalize">{f}</span>)}
              </div>
            </div>
          )}

          {docs.length > 0 && (
            <div className="mt-8" data-testid="prop-docs-section">
              <h2 className="font-display text-xl font-bold">Documenten</h2>
              <p className="text-sm text-slate-500 mt-1">Beschikbare stukken bij deze woning.</p>
              <div className="mt-3 grid sm:grid-cols-2 gap-2">
                {docs.map((d) => (
                  <a key={d.id} href={`${API}/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="p-4 rounded-xl bg-white border border-slate-200 hover:border-[color:var(--wv-green)] flex items-center justify-between gap-3" data-testid={`prop-doc-${d.id}`}>
                    <div className="flex items-start gap-3">
                      <FileText className="w-5 h-5 text-[color:var(--wv-navy)] mt-0.5" />
                      <div>
                        <p className="font-semibold text-slate-900">{DOC_LABELS[d.kind] || d.kind}</p>
                        <p className="text-xs text-slate-500">{d.original_filename} · {Math.round((d.size||0)/1024)} KB</p>
                      </div>
                    </div>
                    <Download className="w-4 h-4 text-slate-400" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {slots.length > 0 && (
            <div className="mt-8" data-testid="prop-slots-section">
              <h2 className="font-display text-xl font-bold">Direct een bezichtiging inplannen</h2>
              <p className="text-sm text-slate-500 mt-1">Kies een tijdslot — bevestiging is direct.</p>
              <div className="mt-3 grid sm:grid-cols-2 md:grid-cols-3 gap-2">
                {slots.map((s) => (
                  <button key={s.id} onClick={() => { setSlotToBook(s); setModal("slot"); }} className="p-4 rounded-xl bg-white border border-slate-200 hover:border-[color:var(--wv-green)] text-left" data-testid={`prop-slot-${s.id}`}>
                    <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[color:var(--wv-green-dark)]" /><p className="font-semibold text-sm">{new Date(s.date).toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" })}</p></div>
                    <p className="font-mono-num text-lg font-bold mt-1">{s.start_time}–{s.end_time}</p>
                    <p className="text-xs text-slate-500">{s.max_bookings - (s.bookings_count || 0)} plek(ken) vrij</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <aside className="md:col-span-1">
          <div className="p-6 rounded-2xl bg-white border border-slate-200 sticky top-24">
            <p className="font-mono-num text-3xl font-extrabold text-[color:var(--wv-navy)]" data-testid="detail-price">{eur(p.price)}</p>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mt-1">k.k.</p>
            <button className="mt-5 w-full wv-btn-green inline-flex items-center justify-center gap-2" onClick={() => setModal("interest")} data-testid="detail-cta-interest">
              <Heart className="w-4 h-4" /> Ik heb interesse
            </button>
            <button className="mt-2 w-full wv-btn-outline inline-flex items-center justify-center gap-2" onClick={() => setModal("viewing")} data-testid="detail-cta-viewing">
              <Calendar className="w-4 h-4" /> Bezichtiging aanvragen
            </button>
            <button className="mt-2 w-full wv-btn-outline inline-flex items-center justify-center gap-2" onClick={() => setModal("offer")} data-testid="detail-cta-offer">
              <Coins className="w-4 h-4" /> Bod uitbrengen
            </button>
            <Link to="/woning-zoeken" className="mt-3 w-full wv-btn-outline inline-flex items-center justify-center" data-testid="detail-cta-profile">
              Maak zoekprofiel
            </Link>
            <p className="mt-4 text-xs text-slate-500">
              Alle Woon Verder-woningen zijn admin-geverifieerd. Contact verloopt via het platform.
            </p>
          </div>
        </aside>
      </div>

      {modal === "offer" && (
        <BidModal
          property={p}
          profileId={new URLSearchParams(window.location.search).get("profile")}
          onClose={() => setModal(null)}
          onSubmitted={() => setModal(null)}
        />
      )}

      {modal && modal !== "offer" && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setModal(null)}>
          <div className="max-w-lg w-full bg-white rounded-2xl" onClick={(e) => e.stopPropagation()} data-testid={`interaction-modal-${modal}`}>
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-display text-xl font-bold">
                {modal === "interest" && "Interesse doorgeven"}
                {modal === "viewing" && "Bezichtiging aanvragen"}
                {modal === "slot" && "Bezichtiging boeken"}
              </h3>
              <button onClick={() => setModal(null)} className="p-1"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-semibold">Naam</label><input className="wv-input mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ix-name" /></div>
                <div><label className="text-sm font-semibold">Email</label><input type="email" className="wv-input mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ix-email" /></div>
                <div className="col-span-2"><label className="text-sm font-semibold">Telefoon (optioneel)</label><input className="wv-input mt-1" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="ix-phone" /></div>
              </div>
              {modal === "interest" && (
                <div><label className="text-sm font-semibold">Uw bericht</label><textarea className="wv-input mt-1" rows="3" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="ix-message" /></div>
              )}
              {modal === "slot" && slotToBook && (
                <div className="p-3 rounded-xl bg-[color:var(--wv-green)]/5 border border-[color:var(--wv-green)]/20">
                  <p className="text-xs uppercase font-bold tracking-wider text-[color:var(--wv-green-dark)]">Gekozen slot</p>
                  <p className="font-display font-bold mt-1">{new Date(slotToBook.date).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" })} · {slotToBook.start_time}–{slotToBook.end_time}</p>
                  <label className="text-sm font-semibold mt-3 block">Opmerking (optioneel)</label>
                  <textarea className="wv-input mt-1" rows="2" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="ix-slot-notes" />
                </div>
              )}
              {modal === "viewing" && (
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-sm font-semibold">Voorkeurdatum</label><input type="date" className="wv-input mt-1" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} data-testid="ix-date" /></div>
                  <div><label className="text-sm font-semibold">Dagdeel</label><select className="wv-input mt-1" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} data-testid="ix-time">{["ochtend","middag","avond"].map(x => <option key={x}>{x}</option>)}</select></div>
                  <div className="col-span-2"><label className="text-sm font-semibold">Opmerkingen</label><textarea className="wv-input mt-1" rows="2" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="ix-notes" /></div>
                </div>
              )}
              {modal === "offer" && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><label className="text-sm font-semibold">Uw bod (€)</label><input type="number" className="wv-input mt-1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="ix-amount" /></div>
                  <div><label className="text-sm font-semibold">Financiering</label>
                    <select className="wv-input mt-1" value={form.financing} onChange={(e) => setForm({ ...form, financing: e.target.value })} data-testid="ix-financing">
                      <option value="eigen_middelen">Eigen middelen</option>
                      <option value="hypotheek_geregeld">Hypotheek geregeld</option>
                      <option value="hypotheek_nodig">Hypotheek nodig</option>
                      <option value="onbekend">Nog niet duidelijk</option>
                    </select>
                  </div>
                  <div><label className="text-sm font-semibold">Overdracht (gewenst)</label><input type="date" className="wv-input mt-1" value={form.transfer_date} onChange={(e) => setForm({ ...form, transfer_date: e.target.value })} data-testid="ix-transfer" /></div>
                  <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={form.financing_reservation} onChange={(e) => setForm({ ...form, financing_reservation: e.target.checked })} data-testid="ix-reservation" /> Onder voorbehoud van financiering</label>
                  <div className="col-span-2"><label className="text-sm font-semibold">Aanvullende voorwaarden</label><textarea className="wv-input mt-1" rows="2" value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} data-testid="ix-conditions" /></div>
                </div>
              )}
              <button onClick={submitInteraction} disabled={busy || !form.name || !form.email} className="w-full wv-btn-green inline-flex items-center justify-center gap-2" data-testid="ix-submit">
                <Send className="w-4 h-4" /> {busy ? "Verzenden…" : "Verzenden"}
              </button>
              <p className="text-xs text-slate-500">Uw gegevens worden alleen gebruikt voor de afhandeling. Zie <Link to="/privacy" className="underline">privacyverklaring</Link>.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
